import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const GDPRComplianceCenter = () => {
  const [selectedRight, setSelectedRight] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [requestForm, setRequestForm] = useState({
    email: '',
    requestType: '',
    description: ''
  });
  
  const formRef = useRef(null);

  useEffect(() => {
    if (selectedRight) {
      setRequestForm(prev => ({ ...prev, requestType: selectedRight }));
    }
  }, [selectedRight]);

  const scrollToForm = () => {
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const dataRights = [
    {
      id: 'access',
      title: 'Right to Access',
      icon: 'Eye',
      description: 'Request a copy of all personal data we hold about you',
      details: 'You have the right to obtain confirmation as to whether we process your personal data and, where that is the case, access to your personal data and related information.'
    },
    {
      id: 'rectification',
      title: 'Right to Rectification',
      icon: 'Edit',
      description: 'Correct inaccurate or incomplete personal data',
      details: 'You have the right to have inaccurate personal data concerning you corrected and incomplete personal data completed.'
    },
    {
      id: 'erasure',
      title: 'Right to Erasure',
      icon: 'Trash2',
      description: 'Request deletion of your personal data ("right to be forgotten")',
      details: 'You have the right to request the deletion of personal data concerning you under certain circumstances, such as when the data is no longer necessary for the purposes for which it was collected.'
    },
    {
      id: 'restriction',
      title: 'Right to Restriction',
      icon: 'Lock',
      description: 'Restrict processing of your personal data',
      details: 'You have the right to restrict the processing of your personal data in specific circumstances, such as when you contest the accuracy of the data or object to its processing.'
    },
    {
      id: 'portability',
      title: 'Right to Data Portability',
      icon: 'Download',
      description: 'Receive your data in a structured, machine-readable format',
      details: 'You have the right to receive personal data concerning you in a structured, commonly used, and machine-readable format and to transmit that data to another controller.'
    },
    {
      id: 'object',
      title: 'Right to Object',
      icon: 'XCircle',
      description: 'Object to processing of your personal data',
      details: 'You have the right to object to the processing of your personal data for direct marketing purposes or where processing is based on legitimate interests.'
    },
    {
      id: 'withdraw',
      title: 'Right to Withdraw Consent',
      icon: 'UserX',
      description: 'Withdraw consent for data processing',
      details: 'Where processing is based on your consent, you have the right to withdraw that consent at any time. This does not affect the lawfulness of processing based on consent before its withdrawal.'
    },
    {
      id: 'complaint',
      title: 'Right to Lodge a Complaint',
      icon: 'AlertTriangle',
      description: 'File a complaint with a supervisory authority',
      details: 'You have the right to lodge a complaint with a supervisory authority, in particular in the EU member state of your habitual residence, place of work, or place of alleged infringement.'
    }
  ];

  const handleSubmitRequest = (e) => {
    e?.preventDefault();
    console.log('GDPR Request submitted:', requestForm);
    setSuccessMessage('Your request has been submitted successfully. Our DPO team will review it and respond to you within 30 days as required by GDPR.');
    setRequestForm({ email: '', requestType: '', description: '' });
  };

  const handleExportData = () => {
    alert('Data export initiated. You will receive an email with a secure link to download your data within 30 days.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-6 py-16">
          <Link
            to="/"
            className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
          >
            <Icon name="ArrowLeft" size={20} className="mr-2" />
            Back to Home
          </Link>
          
          <div className="max-w-4xl">
            <div className="flex items-center mb-4">
              <Icon name="Shield" size={48} className="mr-4" />
              <h1 className="text-4xl lg:text-5xl font-bold">
                GDPR Compliance Center
              </h1>
            </div>
            <p className="text-xl text-white/90 mb-6">
              Exercise your data protection rights under the General Data Protection Regulation (GDPR)
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 inline-block">
              <div className="flex items-center text-white">
                <Icon name="Info" size={20} className="mr-3" />
                <span className="text-sm">
                  We respond to all requests within 30 days as required by GDPR
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">
              Quick Actions
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              <button
                onClick={handleExportData}
                className="flex items-center p-4 border-2 border-primary rounded-xl hover:bg-primary/5 transition-colors"
              >
                <div className="bg-primary/10 rounded-lg p-3 mr-4">
                  <Icon name="Download" size={24} className="text-primary" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-slate-800">Export My Data</div>
                  <div className="text-sm text-slate-600">Download all your data</div>
                </div>
              </button>

              <button
                onClick={() => {
                  setSelectedRight('erasure');
                  setSuccessMessage('');
                  scrollToForm();
                }}
                className="flex items-center p-4 border-2 border-slate-200 rounded-xl hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <div className="bg-slate-100 rounded-lg p-3 mr-4">
                  <Icon name="Trash2" size={24} className="text-slate-600" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-slate-800">Delete My Account</div>
                  <div className="text-sm text-slate-600">Remove all your data</div>
                </div>
              </button>

              <Link
                to="/privacy-policy"
                className="flex items-center p-4 border-2 border-slate-200 rounded-xl hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <div className="bg-slate-100 rounded-lg p-3 mr-4">
                  <Icon name="FileText" size={24} className="text-slate-600" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-slate-800">Privacy Policy</div>
                  <div className="text-sm text-slate-600">Read our full policy</div>
                </div>
              </Link>
            </div>
          </div>

          {/* Your GDPR Rights */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">
              Your GDPR Rights
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {dataRights?.map((right) => (
                <div
                  key={right?.id}
                  onClick={() => {
                    setSelectedRight(right?.id);
                    setSuccessMessage('');
                  }}
                  className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                    selectedRight === right?.id
                      ? 'border-primary bg-primary/5' :'border-slate-200 hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-start mb-4">
                    <div className={`rounded-lg p-3 mr-4 ${
                      selectedRight === right?.id
                        ? 'bg-primary/20' :'bg-slate-100'
                    }`}>
                      <Icon 
                        name={right?.icon} 
                        size={24} 
                        className={selectedRight === right?.id ? 'text-primary' : 'text-slate-600'} 
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800 mb-2">
                        {right?.title}
                      </h3>
                      <p className="text-sm text-slate-600 mb-3">
                        {right?.description}
                      </p>
                      {selectedRight === right?.id && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                          <p className="text-xs text-slate-700">
                            {right?.details}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedRight === right?.id && (
                    <Button
                      variant="default"
                      size="sm"
                      className="w-full mt-4"
                      iconName="ArrowDown"
                      iconPosition="right"
                      onClick={(e) => {
                        e.stopPropagation();
                        scrollToForm();
                      }}
                    >
                      Fill out Form Below
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Request Form / Success State */}
          {selectedRight && (
            <div ref={formRef} className="bg-white rounded-2xl shadow-lg p-8 mb-8 scroll-mt-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-800">
                  {successMessage ? 'Request Submitted' : 'Submit Your Request'}
                </h2>
                <button
                  onClick={() => {
                    setSelectedRight(null);
                    setSuccessMessage('');
                  }}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <Icon name="X" size={24} />
                </button>
              </div>
              
              {successMessage ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <div className="mx-auto bg-green-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                    <Icon name="Check" size={24} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-green-800 mb-2">Success</h3>
                  <p className="text-green-700">{successMessage}</p>
                  <Button
                    variant="outline"
                    className="mt-6"
                    onClick={() => {
                      setSelectedRight(null);
                      setSuccessMessage('');
                    }}
                  >
                    Done
                  </Button>
                </div>
              ) : (
              <form onSubmit={handleSubmitRequest} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    required
                    placeholder="your.email@example.com"
                    value={requestForm?.email}
                    onChange={(e) => setRequestForm({ ...requestForm, email: e?.target?.value })}
                    className="w-full"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    We'll use this email to verify your identity and send you updates
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Request Type
                  </label>
                  <select
                    required
                    value={requestForm?.requestType}
                    onChange={(e) => setRequestForm({ ...requestForm, requestType: e?.target?.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="">Select a request type</option>
                    {dataRights?.map((right) => (
                      <option key={right?.id} value={right?.id}>
                        {right?.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Additional Details
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Please provide any additional information to help us process your request..."
                    value={requestForm?.description}
                    onChange={(e) => setRequestForm({ ...requestForm, description: e?.target?.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <Icon name="AlertCircle" size={20} className="text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-slate-700">
                      <strong>Processing Time:</strong> We will respond to your request within 30 days as required by GDPR. 
                      You may be asked to verify your identity before we process your request.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button
                    type="submit"
                    variant="default"
                    className="flex-1"
                    iconName="Send"
                    iconPosition="right"
                  >
                    Submit Request
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setSelectedRight(null);
                      setSuccessMessage('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
              )}
            </div>
          )}

          {/* Data Protection Officer Contact */}
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl shadow-lg p-8 text-white">
            <div className="flex items-start">
              <Icon name="User" size={32} className="mr-4 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold mb-4">
                  Contact Our Data Protection Officer
                </h2>
                <p className="text-white/90 mb-6">
                  For complex GDPR inquiries or if you need assistance with your data protection rights, 
                  you can contact our Data Protection Officer directly.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center">
                    <Icon name="Mail" size={18} className="mr-3" />
                    <span>dpo@opengadget.com</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Phone" size={18} className="mr-3" />
                    <span>+1 (555) 123-4567</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="MapPin" size={18} className="mr-3" />
                    <span>Data Protection Officer, OpenGadget Inc., Privacy Department</span>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  iconName="ExternalLink"
                  iconPosition="right"
                  onClick={() => window.location.href = '/support-center'}
                >
                  Visit Support Center
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GDPRComplianceCenter;